import {
  createRequestLineStringCoordinates,
  extractLineStringCoordinatesAndRanges,
} from "@/cache/CustomGeoJSONCache/lineStringHelper";
import {
  advancedThresholdValuePropertyName,
  aggregateOfListForLine,
  roundThresholdValue,
} from "@/cache/CustomGeoJSONCache/utils";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import type { RetryState } from "@/cache/SpatioTemporalTileCache/RetryState";
import { customGeoJsonLayerConfig } from "@/constants/layerConfigAttributes";
import type { CustomGeoJsonRequestOption } from "@/layers/geojson/custom";
import type { AdvancedThreshold } from "@/overlay/components/CommonParts/Threshold/types";
import { addMultipleValuesToFeature } from "@/threads/DataProcessingThread/utils/custom-geojson";
import { chunkAndBatchesLineString } from "@/threads/DataProcessingThread/utils/custom-geojson/chunkAndBatchesLineString";
import { Aggregate } from "@mm/api.meteomatics.com";
import type { Feature, GeoJsonProperties, Geometry, LineString } from "geojson";
import { type DateTime, Duration } from "luxon";

export function hydrateLineStringFeaturesAdvancedThreshold(
  features: Array<Feature<LineString, GeoJsonProperties>>,
  datetime: DateTime,
  customGeoJsonRequestOption: CustomGeoJsonRequestOption,
  retry: RetryState,
): Promise<Feature<Geometry, GeoJsonProperties>[]>[] {
  const tmpAdvanced_thresholds: AdvancedThreshold[] = customGeoJsonRequestOption.advancedThresholds;

  const segmentLength =
    customGeoJsonRequestOption.customOption.segmentLength || customGeoJsonLayerConfig.defaultLineSegmentLength;
  const units = customGeoJsonRequestOption.customOption.units || customGeoJsonLayerConfig.defaultLineSegmentUnit;
  const lineStringBatches = chunkAndBatchesLineString(features, segmentLength, units);

  const promises: Promise<any[]>[] = [];
  lineStringBatches.forEach((batch, batchIndex) => {
    const correspondingLineFeatures = batch.map((_, index) => {
      return lineStringBatches[batchIndex][index];
    });
    const { coordinates: lineStringCoordinates, indexRanges: lineStringIndexRanges } =
      extractLineStringCoordinatesAndRanges(batch);

    const thresholdDetails = tmpAdvanced_thresholds
      .flatMap((t) => t.details ?? [])
      .filter((detail) => detail.parameter && detail.model);

    const thresholdDetailsPromises: Promise<Feature<Geometry, GeoJsonProperties>[]>[] = thresholdDetails.map(
      (detail) => {
        return apiThreadPool
          .GetPointJSON(
            createRequestLineStringCoordinates(
              lineStringCoordinates,
              detail.parameter,
              detail.model,
              datetime.plus(Duration.fromObject({ minutes: detail.offsetMinutes })),
            ),
            retry,
            { forcePostRequest: true, preventRequestSplitter: true },
          )
          .then(({ data }) => {
            return correspondingLineFeatures.map((feature, index) => {
              const valuesByLineString = lineStringIndexRanges.map(({ startIndex, endIndex }) => {
                return data[0].coordinates
                  .slice(startIndex, endIndex + 1)
                  .map((datesValue) => datesValue.dates[0].value as number);
              });
              const aggregate = detail.aggregate ?? Aggregate.max;
              const singleValue = aggregateOfListForLine(aggregate, valuesByLineString[index]);
              if (singleValue != null) {
                return addMultipleValuesToFeature(feature, {
                  // there comes often to long decimal value from aggregation calculation
                  [advancedThresholdValuePropertyName(detail.id)]: roundThresholdValue(singleValue),
                });
              }
              return feature;
            });
          });
      },
    );
    const finalPromise: Promise<Feature<Geometry, GeoJsonProperties>[]> = Promise.allSettled(
      thresholdDetailsPromises,
    ).then((results) => {
      const fulfilledValues = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => (result as PromiseFulfilledResult<any>).value);

      return fulfilledValues[0];
    });
    promises.push(finalPromise);
  });

  return promises;
}
